import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Leaf, Medal } from 'lucide-react';
import TextType from './TextType';

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  className: string;
  lineClassName?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, subtitle, className, lineClassName }) => (
  <motion.div 
    variants={{
      hidden: { opacity: 0, scale: 0.8, y: 20 },
      visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5 } }
    }}
    className={`absolute flex items-center gap-3 px-4 py-3 rounded-xl backdrop-blur-sm ${className}`}
    style={{
      background: '#FE93D229',
      border: '0.5px solid #EC008C1F'
    }}
  >
    <div className="bg-[#FF4D6D] p-2 rounded-lg text-white">
      <Icon size={18} />
    </div>
    <div>
      <h3 className="font-heading font-bold text-text-main text-sm">{title}</h3>
      <p className="text-xs text-text-muted">{subtitle}</p>
    </div>
    {lineClassName && (
      <div className={`absolute border-t border-dashed border-gray-300 -z-10 ${lineClassName}`} />
    )}
  </motion.div>
);

export const PackagingShowcase: React.FC = () => {
  return (
    <section className="py-12 bg-[#FFF5F7] overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6 }}
            className="flex justify-center mb-6"
          >
             <img src="/Images/s2_client.webp" alt="Clients" className="h-12 object-contain" />
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-heading font-bold text-3xl md:text-4xl text-text-main leading-tight text-center"
          >
            <TextType
              baseOpacity={0}
              enableBlur={true}
              baseRotation={5}
              blurStrength={10}
              size="lg"
              duration={1}
              textClassName="font-heading font-bold text-lg md:text-3xl text-text-main leading-tight text-center"
            >
              Premium mono-cartons, powered by modern technology and precise, sustainable manufacturing.
            </TextType>
          </motion.h2>
        </div>

        {/* Main Content */}
        <div className="relative max-w-7xl mx-auto min-h-[auto] lg:min-h-[700px] xl:min-h-[850px] flex flex-col lg:block items-center justify-center">
          {/* Central Image */}
          <div className="relative z-10 w-full max-w-[350px] lg:max-w-[550px] xl:max-w-[650px] mx-auto lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 mb-12 lg:mb-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-full"
            >
              <img 
                src="/Images/h_packaging.webp" 
                alt="Premium Packaging Structure" 
                className="w-full h-auto drop-shadow-2xl"
              />
            </motion.div>
          </div>

          {/* Feature Cards Grid for Mobile/Tablet / Absolute for Desktop */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full lg:block"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.15
                }
              }
            }}
          >
            {/* Top Left */}
            <FeatureCard 
              icon={Trophy}
              title="High Quality"
              subtitle="Crafted from top materials"
              className="relative lg:absolute lg:top-[20%] lg:left-0 w-full lg:w-auto justify-start"
              lineClassName="hidden lg:block w-24 xl:w-48 top-1/2 -right-24 xl:-right-48"
            />

            {/* Middle Left */}
            <FeatureCard 
              icon={Medal}
              title="High Quality"
              subtitle="Crafted from top materials"
              className="relative lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:left-0 w-full lg:w-auto justify-start"
              lineClassName="hidden lg:block w-32 xl:w-56 top-1/2 -right-32 xl:-right-56"
            />

            {/* Bottom Left */}
            <FeatureCard 
              icon={Trophy}
              title="High Quality"
              subtitle="Crafted from top materials"
              className="relative lg:absolute lg:bottom-[20%] lg:left-0 w-full lg:w-auto justify-start"
              lineClassName="hidden lg:block w-24 xl:w-48 top-1/2 -right-24 xl:-right-48"
            />

            {/* Top Right */}
            <FeatureCard 
              icon={Leaf}
              title="Sustainable"
              subtitle="Crafted from top materials"
              className="relative lg:absolute lg:top-[20%] lg:right-0 w-full lg:w-auto justify-start"
              lineClassName="hidden lg:block w-24 xl:w-48 top-1/2 -left-24 xl:-left-48"
            />

            {/* Middle Right */}
            <FeatureCard 
              icon={Leaf}
              title="Sustainable"
              subtitle="Crafted from top materials"
              className="relative lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:right-0 w-full lg:w-auto justify-start"
              lineClassName="hidden lg:block w-32 xl:w-56 top-1/2 -left-32 xl:-left-56"
            />

            {/* Bottom Right */}
            <FeatureCard 
              icon={Trophy}
              title="Premium Finishing"
              subtitle="Crafted from top materials"
              className="relative lg:absolute lg:bottom-[20%] lg:right-0 w-full lg:w-auto justify-start"
              lineClassName="hidden lg:block w-24 xl:w-48 top-1/2 -left-24 xl:-left-48"
            />
          </motion.div>

        </div>
      </div>
    </section>
  );
};
